import React from 'react';

import { BadgeTone } from '@/components/Badge/Badge';
import { SiAggregateStatus } from '@/services/api/scheduled-instant';

/**
 * Presentational bits shared by the Scheduled Instant screens.
 *
 * These lived inside index.tsx while there was only one screen. Screen 14
 * (metrics) and Screen 15 (attention riders) need the same vocabulary, and
 * copying it would let the three screens drift apart — the status colours in
 * particular have to agree, or the same rider reads as a different severity
 * depending on which screen ops happens to be looking at.
 */

export interface AggMeta {
  tone: BadgeTone;
  label: string;
  block: string;
}

export const AGG: Record<SiAggregateStatus, AggMeta> = {
  confirmed: {
    tone: 'success',
    label: 'Confirmed',
    block: 'bg-green-50 border-l-green-500 text-green-800',
  },
  pending: {
    tone: 'amber',
    label: 'Pending',
    block: 'bg-amber-50 border-l-amber-500 text-amber-800',
  },
  no_response: {
    tone: 'danger',
    label: 'No Response',
    block: 'bg-red-50 border-l-red-500 text-red-800',
  },
  unstaffed: {
    tone: 'amber',
    label: 'Belum ada rider',
    block:
      'bg-amber-50/50 border border-dashed border-amber-400 border-l-amber-400 text-amber-700 italic',
  },
};

export const initials = (n: string) =>
  n
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

export const Avatar: React.FC<{ name: string; reserve?: boolean }> = ({
  name,
  reserve,
}) => (
  <span
    className={`w-6 h-6 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0 ${
      reserve
        ? 'bg-primary text-white'
        : 'bg-(--theme-accent-alpha-10) text-accent'
    }`}
  >
    {initials(name)}
  </span>
);

/** Compact ops stat cell (denser + consistent vs the chunky shared StatCard). */
export const StatCell: React.FC<{
  label: string;
  value: number | string;
  tone: 'ink' | 'amber' | 'red' | 'green';
  /** Optional sub-line for context the number alone would misrepresent. */
  hint?: string;
  /** Lets tests scope to one tile; several tiles can show the same number. */
  testId?: string;
  /** When set, the whole cell becomes a button (drill-down affordance). */
  onClick?: () => void;
}> = ({ label, value, tone, hint, testId, onClick }) => {
  const color =
    value !== 0 && tone !== 'ink'
      ? {
          amber: 'text-amber-600',
          red: 'text-red-600',
          green: 'text-green-600',
        }[tone]
      : 'text-ink';
  const body = (
    <>
      <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-400 flex items-center gap-1">
        {label}
        {onClick && (
          <i className="ri-arrow-right-up-line text-gray-300 group-hover:text-gray-500" />
        )}
      </div>
      <div className={`text-2xl font-bold tabular mt-0.5 ${color}`}>{value}</div>
      {hint && <div className="text-[11px] text-gray-400 mt-0.5">{hint}</div>}
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={testId}
        className="group w-full px-5 py-3.5 text-left transition-colors hover:bg-gray-50"
      >
        {body}
      </button>
    );
  }
  return (
    <div className="px-5 py-3.5" data-testid={testId}>
      {body}
    </div>
  );
};

/** Panel = Card's outer shell without the forced p-6 (for edge-to-edge tables). */
export const Panel: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className = '' }) => (
  <div
    className={`bg-white shadow-sm rounded-gsm border border-gray-200 overflow-hidden ${className}`}
  >
    {children}
  </div>
);

/**
 * Share of a slot's riders that are ready to go (`counts.green` over total).
 *
 * Returns null for an unstaffed slot rather than 0% — "no riders assigned" and
 * "riders assigned, none confirmed" are different ops problems, and 0% would
 * hide the first behind the second. Also avoids dividing by zero.
 */
export const readyShare = (
  green: number,
  riderCount: number
): number | null => (riderCount > 0 ? green / riderCount : null);
